import React, { useEffect, useState } from 'react';
import HistoryIcon from '@material-ui/icons/History';
import PersonIcon from '@material-ui/icons/Person';
import SecurityIcon from '@material-ui/icons/Security';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import { Link, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { fetchYowanAdminNotificationRecipientDetail } from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const readOnlyGridStyle = {
  display: 'grid',
  gap: '0.85rem',
};

const tableStyle = {
  minWidth: '60rem',
};

const relatedActionStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(13rem, 1fr))',
  gap: '0.7rem',
};

const relatedBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.45rem',
  minHeight: '2.6rem',
  padding: '0.55rem 0.85rem',
  border: '1px solid rgba(71, 86, 67, 0.18)',
  borderRadius: '999px',
  background: 'rgba(255, 255, 255, 0.74)',
  color: 'var(--text-main)',
  fontSize: 'var(--font-size-small)',
  fontWeight: 700,
  textAlign: 'center',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const relatedBadgeIconStyle = {
  flex: '0 0 auto',
  fontSize: '1.05rem',
};

const ReadOnlyField = ({ label, value, multiline = false, rows = 4 }) => {
  const fieldValue = value || '-';

  return (
    <label className="c-form__field" style={{ cursor: 'not-allowed' }}>
      <span className="c-form__label">{label}</span>
      {multiline ? (
        <textarea
          className="c-form__control c-form__control--textarea"
          readOnly
          rows={rows}
          style={{ cursor: 'not-allowed' }}
          value={fieldValue}
        />
      ) : (
        <input
          className="c-form__control"
          readOnly
          style={{ cursor: 'not-allowed' }}
          type="text"
          value={fieldValue}
        />
      )}
    </label>
  );
};

const buildRecipientHistoryHref = (email) => {
  const query = email ? `?recipient_email=${encodeURIComponent(email)}` : '';

  return `/admin/notifications${query}`;
};

const buildAudienceGroupHref = (audienceGroupKey) =>
  `/admin/notifications/recipients?tab=groups&group_key=${encodeURIComponent(audienceGroupKey)}`;

const memberSourceKinds = new Set(['member', 'yowan_member', 'yowan_member_profile']);
const memberContextKinds = new Set(['member', 'member_profile', 'yowan_member']);
const blogAuthorSourceKinds = new Set(['blog_author', 'yowan_blog_author', 'blog_author_access', 'yowan_blog_author_access']);
const blogAuthorContextKinds = new Set(['blog_author', 'yowan_blog_author', 'blog_author_access', 'yowan_blog_author_access']);

const findMemberSource = (sources = []) =>
  sources.find((source) =>
    (memberSourceKinds.has(source.sourceKind) || memberContextKinds.has(source.contextKind)) &&
    (source.sourceId || source.contextId),
  );

const buildMemberHref = (source) => {
  const memberId = source?.sourceId || source?.contextId || '';

  return memberId ? `/admin/members/${memberId}/edit` : '';
};

const hasBlogAuthorSource = (sources = []) =>
  sources.some((source) =>
    blogAuthorSourceKinds.has(source.sourceKind) || blogAuthorContextKinds.has(source.contextKind),
  );

const formatRecipientSourceContext = (source = {}) =>
  [
    source.contextKindLabel || source.contextKind,
    source.contextId,
  ].filter(Boolean).join(' / ') || '-';

const RelatedBadgeLink = ({ to, icon, children }) => (
  <Link to={to} style={relatedBadgeStyle}>
    {React.cloneElement(icon, { style: relatedBadgeIconStyle, 'aria-hidden': true })}
    <span>{children}</span>
  </Link>
);

const AdminNotificationRecipientDetailPage = () => {
  const content = yowanPageContent['/admin/notifications/recipients/:recipientId'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const { recipientId } = useParams();
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanAdminNotificationRecipientDetail(recipientId)
      .then((payload) => {
        if (isMounted) {
          setRecord(payload);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRecord(null);
          setLoadError('通知宛先を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [recipientId]);

  if (!content) {
    return null;
  }

  const backToRecipients = appendYowanPreviewQueryIfNeeded('/admin/notifications/recipients', preservedDomainSearch);
  const recipientHistoryHref = appendYowanPreviewQueryIfNeeded(
    buildRecipientHistoryHref(record?.email),
    preservedDomainSearch,
  );

  if (!record) {
    return (
      <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
        <AdminPageHeader
          title={content.header.title}
          paragraphs={isLoading ? [] : [loadError || '指定された通知宛先は見つかりませんでした。']}
          actions={[
            <Link key="back-to-recipients" to={backToRecipients}>
              通知宛先へ戻る
            </Link>,
          ]}
        />
        {isLoading ? <LoadingSpinner /> : null}
      </AdminBlogShell>
    );
  }

  const memberHref = buildMemberHref(findMemberSource(record.sources));
  const memberAccessHref = appendYowanPreviewQueryIfNeeded('/admin/access/member', preservedDomainSearch);
  const operatorAccessHref = appendYowanPreviewQueryIfNeeded('/admin/access/operator', preservedDomainSearch);
  const blogAuthorsHref = appendYowanPreviewQueryIfNeeded('/admin/content/blog-authors', preservedDomainSearch);
  const showBlogAuthorsLink = hasBlogAuthorSource(record.sources);

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={`${record.displayName || record.maskedEmail || record.email} / 通知宛先`}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-recipients" to={backToRecipients}>
            通知宛先へ戻る
          </Link>,
          <Link key="back-to-policies" to={appendYowanPreviewQueryIfNeeded('/admin/notifications/policies', preservedDomainSearch)}>
            通知ルールへ
          </Link>,
        ]}
      />

      <section className="yowan-admin-form c-surface-content" style={readOnlyGridStyle}>
        <ReadOnlyField label="宛先ID" value={record.id} />
        <ReadOnlyField label="メールアドレス" value={record.email} />
        <ReadOnlyField label="表示名" value={record.displayName} />
        <ReadOnlyField label="状態" value={record.isActive ? '有効' : '無効'} />
        <ReadOnlyField label="作成日" value={record.createdAt} />
        <ReadOnlyField label="更新日" value={record.updatedAt} />
      </section>

      <section className="yowan-admin-stack">
        <AdminPageHeader
          title="関連導線"
          paragraphs={['この宛先に紐づく通知履歴と、関連する管理領域を確認します。']}
        />
        <div className="c-surface-content" style={relatedActionStyle}>
          <RelatedBadgeLink to={recipientHistoryHref} icon={<HistoryIcon />}>
            この宛先の通知履歴
          </RelatedBadgeLink>
          {memberHref ? (
            <RelatedBadgeLink
              to={appendYowanPreviewQueryIfNeeded(memberHref, preservedDomainSearch)}
              icon={<PersonIcon />}
            >
              会員を確認
            </RelatedBadgeLink>
          ) : null}
          <RelatedBadgeLink to={memberAccessHref} icon={<VerifiedUserIcon />}>
            会員資格一覧へ
          </RelatedBadgeLink>
          <RelatedBadgeLink to={operatorAccessHref} icon={<SecurityIcon />}>
            管理権限一覧へ
          </RelatedBadgeLink>
          {showBlogAuthorsLink ? (
            <RelatedBadgeLink to={blogAuthorsHref} icon={<PersonIcon />}>
              ブログ執筆者一覧へ
            </RelatedBadgeLink>
          ) : null}
        </div>
      </section>

      <section className="yowan-admin-stack">
        <AdminPageHeader
          title="受信設定"
          paragraphs={['カテゴリごとの現在の受信可否を確認します。変更はマイページまたは配信停止URLから行われます。']}
        />
        <div className="yowan-admin-resource-table-wrap c-surface-content">
          <table className="yowan-admin-resource-table" style={tableStyle}>
            <thead>
              <tr>
                <th scope="col">カテゴリ</th>
                <th scope="col">状態</th>
                <th scope="col">停止日時</th>
                <th scope="col">更新日</th>
                <th scope="col">確認</th>
              </tr>
            </thead>
            <tbody>
              {record.preferences.map((preference) => (
                <tr key={preference.id || preference.categoryKey}>
                  <td>{preference.categoryLabel}</td>
                  <td>{preference.isEnabled ? '受信する' : '停止中'}</td>
                  <td>{preference.disabledAt}</td>
                  <td>{preference.updatedAt}</td>
                  <td>
                    {preference.audienceGroupKey ? (
                      <Link
                        to={appendYowanPreviewQueryIfNeeded(
                          buildAudienceGroupHref(preference.audienceGroupKey),
                          preservedDomainSearch,
                        )}
                      >
                        {preference.audienceGroupLabel || '宛先グループ'}
                      </Link>
                    ) : (
                      <span className="yowan-admin-resource-table__subline">準備中</span>
                    )}
                  </td>
                </tr>
              ))}
              {!record.preferences.length ? (
                <tr>
                  <td colSpan="5">受信設定はまだありません。</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="yowan-admin-stack">
        <AdminPageHeader
          title="宛先生成元"
          paragraphs={['この宛先がどの領域から同期されたかを確認するための監査・診断情報です。']}
        />
        <div className="yowan-admin-resource-table-wrap c-surface-content">
          <table className="yowan-admin-resource-table" style={tableStyle}>
            <thead>
              <tr>
                <th scope="col">表示名</th>
                <th scope="col">種別</th>
                <th scope="col">生成元ID</th>
                <th scope="col">関連情報</th>
                <th scope="col">主生成元</th>
              </tr>
            </thead>
            <tbody>
              {record.sources.map((source) => (
                <tr key={source.id || `${source.sourceKind}-${source.sourceId}`}>
                  <td>{source.displayName || '-'}</td>
                  <td>{source.sourceKindLabel || source.sourceKind || '-'}</td>
                  <td>
                    {source.sourceDetailHref && source.sourceId ? (
                      <Link
                        to={appendYowanPreviewQueryIfNeeded(source.sourceDetailHref, preservedDomainSearch)}
                      >
                        {source.sourceId}
                      </Link>
                    ) : (
                      source.sourceId || '-'
                    )}
                  </td>
                  <td>{formatRecipientSourceContext(source)}</td>
                  <td>{source.isPrimary ? 'はい' : 'いいえ'}</td>
                </tr>
              ))}
              {!record.sources.length ? (
                <tr>
                  <td colSpan="5">宛先生成元情報はまだありません。</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </AdminBlogShell>
  );
};

export default AdminNotificationRecipientDetailPage;
